import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getCategoryDisplay } from '../config/categoryConfig'
import { DataLayer } from '../lib/dataLayer'
import { ExerciseCategory } from '../types/database'

type Exercise = Awaited<ReturnType<typeof DataLayer.getExercises>>[number]

interface ExerciseCardStats {
  totalCards: number
  masteredCards: number
}

interface StudyAreaViewProps {
  areaId: string
  onBack: () => void
  onSelectExercise: (exerciseId: string) => void
}

const iconPaths: Record<string, string> = {
  'book-open':
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  'academic-cap':
    'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5',
  'play-circle':
    'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z',
}

const colorClasses: Record<string, { bg: string; text: string; progressBg: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', progressBg: 'bg-emerald-500' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', progressBg: 'bg-indigo-500' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', progressBg: 'bg-purple-500' },
}

export function StudyAreaView({ areaId, onBack, onSelectExercise }: StudyAreaViewProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [exerciseStats, setExerciseStats] = useState<Map<string, ExerciseCardStats>>(new Map())

  const category = getCategoryDisplay(areaId as ExerciseCategory)

  useEffect(() => {
    async function loadData() {
      if (!user || !category) return

      setLoading(true)
      try {
        const [catExercises, allCards, progress] = await Promise.all([
          DataLayer.getExercisesByCategory(category.id),
          DataLayer.getCards(),
          DataLayer.getUserCardProgress(user.id),
        ])

        const progressMap = new Map(progress.map(p => [p.card_id, p]))
        const statsMap = new Map<string, ExerciseCardStats>()

        for (const exercise of catExercises) {
          const exerciseCards = allCards.filter(c => c.exercise_id === exercise.id)
          let mastered = 0
          for (const card of exerciseCards) {
            const p = progressMap.get(card.id)
            if (p && p.status === 'mastered') mastered++
          }
          statsMap.set(exercise.id, { totalCards: exerciseCards.length, masteredCards: mastered })
        }

        setExercises(catExercises)
        setExerciseStats(statsMap)
      } catch (error) {
        console.error('Failed to load category data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [areaId, user, category])

  if (!category) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-gray-500">Category not found</p>
        <button onClick={onBack} className="mt-4 text-indigo-600 hover:text-indigo-800">Go back</button>
      </div>
    )
  }

  const colors = colorClasses[category.color] || colorClasses.emerald
  const iconPath = iconPaths[category.icon] || iconPaths['book-open']

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="space-y-4">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Category Header */}
      <div className={`${colors.bg} rounded-xl p-6 mb-6`}>
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/50 transition-colors"
            aria-label="Go back"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <div className="p-3 rounded-lg bg-white shadow-sm">
            <svg className={`w-8 h-8 ${colors.text}`} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{category.name}</h1>
            <p className="text-gray-600 mt-1">{category.description}</p>
          </div>
        </div>
      </div>

      {/* Exercise Cards */}
      {exercises.length > 0 ? (
        <div className="space-y-3">
          {exercises.map((exercise) => {
            const stats = exerciseStats.get(exercise.id)
            const progressPercent = stats && stats.totalCards > 0
              ? Math.round((stats.masteredCards / stats.totalCards) * 100)
              : 0

            return (
              <button
                key={exercise.id}
                onClick={() => onSelectExercise(exercise.id)}
                className="w-full text-left p-5 rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{exercise.name}</h3>
                      {exercise.level && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {exercise.level}
                        </span>
                      )}
                    </div>
                    {exercise.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{exercise.description}</p>
                    )}
                    {stats && stats.totalCards > 0 && (
                      <div className="mt-3">
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden w-48">
                          <div
                            className={`h-full ${colors.progressBg} transition-all duration-300`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 mt-1">
                          {stats.masteredCards}/{stats.totalCards} mastered
                        </span>
                      </div>
                    )}
                  </div>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p>No exercises in this category yet.</p>
        </div>
      )}
    </div>
  )
}
